import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  alpha,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  Analytics as AnalyticsIcon,
  CalendarMonth,
  Dashboard,
  Rocket,
  TrendingUp,
  Close,
  Visibility,
  Phone,
  PhoneDisabled,
  PhoneCallback,
  PhoneForwarded,
  Edit,
  Assignment,
  Clear,
  AssignmentTurnedIn,
  AccountBalanceWallet,
  ReportProblem,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { url } from "../constants/url";
import { useThemeToggle } from "../context/ThemeContext";

const Reports = ({ userData }) => {
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const [exporting, setExporting] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [exportType, setExportType] = useState("all");
  const [adminCompany, setAdminCompany] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [leadsData, setLeadsData] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  // Month and Year selection states
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const UID = userData?._id;
  const role = userData?.role;

  // Theme colors from context
  const primaryColor = theme.palette.primary.main;
  const themeLight = alpha(primaryColor, 0.06);
  const themeLighter = alpha(primaryColor, 0.03);
  const themeBorder = alpha(primaryColor, 0.1);

  // Status display mapping (same as leads screen)
  const statusDisplayMap = {
    hot: "Hot",
    warm: "Warm",
    cold: "Cold",
    closed: "Closed",
    dead: "Dead",
    rescheduled: "Rescheduled",
    quotation_shared: "Quotation Shared",
    quotation_approved: "Quotation Approved",
    quotation_rejected: "Quotation Rejected",
    meeting_scheduled: "Meeting Scheduled",
    call_not_received: "Not Received",
    received: "Received",
    switch_off: "Switch Off",
    wrong_number: "Wrong Number",
    negotiation: "Negotiation",
    "follow-up": "Follow Up",
    rejected: "Rejected",
    fully_paid: "Fully Paid",
    partially_paid: "Partially Paid",
    quotation_expired: "Quotation Expired",
    quotation_revised: "Quotation Revised",
    token_received: "Token Received",
    token_paid: "Token Paid",
    payment_overdue: "Payment Overdue",
    pending: "Payment Pending",
    overdue: "Overdue Payment",
  };

  // Month options
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  // Year options (current year and previous 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchCompaniesData();
  }, [role]);

  // Auto-dismiss alerts after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchCompaniesData = async () => {
    setLoadingCompanies(true);
    setError("");
    try {
      if (role === "superAdmin") {
        const response = await fetch(`${url}/api/company/fetch-companies`);
        const data = await response.json();
        if (data.data) {
          setCompanies(data.data);
          if (data.data.length > 0) setSelectedCompany(data.data[0]._id);
        }
      } else if (role === "admin") {
        const response = await fetch(`${url}/api/company/fetch-companies?userId=${UID}`);
        const data = await response.json();
        if (data.data && data.data.length > 0) {
          const companyData = Array.isArray(data.data) ? data.data[0] : data.data;
          setAdminCompany(companyData);
          setSelectedCompany(companyData._id);
          setCompanies([companyData]);
        }
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
      setError("Failed to load company information");
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Fetch leads data based on status and month/year
  const fetchLeadsData = async (statusType = "all") => {
    setLoadingLeads(true);
    setError("");
    setActiveFilter(statusType);

    try {
      const fetchData = {
        userId: UID,
        role,
        month: selectedMonth,
        year: selectedYear,
        status: statusType,
      };

      if (role === "superAdmin" && selectedCompany && selectedCompany !== "all") {
        fetchData.companyId = selectedCompany;
      } else if (role === "admin" && adminCompany?._id) {
        fetchData.companyId = adminCompany._id;
      }

      console.log("Fetching leads with:", fetchData);

      const response = await fetch(`${url}/api/reports/leads-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fetchData),
      });

      if (response.ok) {
        const data = await response.json();
        setLeadsData(data.data || []);
        setSuccess(`Loaded ${data.data?.length || 0} ${statusType} leads for ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`);
      } else {
        const errorData = await response.json().catch(() => null);
        setError(errorData?.message || "Failed to fetch leads data");
      }
    } catch (err) {
      setError("Error fetching leads: " + err.message);
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    setError("");
    setSuccess("");

    try {
      const exportData = {
        userId: UID,
        role,
        month: selectedMonth,
        year: selectedYear,
        exportType,
      };

      if (role === "superAdmin" && selectedCompany && selectedCompany !== "all") {
        exportData.companyId = selectedCompany;
      } else if (role === "admin" && adminCompany?._id) {
        exportData.companyId = adminCompany._id;
      }

      const response = await fetch(`${url}/api/export/export-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(exportData),
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = downloadUrl;

        // Generate filename
        const monthName = months.find(m => m.value === selectedMonth)?.label || selectedMonth;
        let fileName = `Leads_Report_${monthName}_${selectedYear}.xlsx`;

        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);

        setSuccess(`Excel report for ${monthName} ${selectedYear} downloaded successfully!`);
      } else {
        const errorData = await response.json().catch(() => null);
        setError(errorData?.message || "Export failed");
      }
    } catch (err) {
      setError("Export error: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Clear active filter
  const clearFilter = () => {
    setActiveFilter(null);
    setLeadsData([]);
  };

  // Quick action buttons
  const getQuickActionButtons = () => {
    return [
      { type: "hot", label: "Hot Leads", color: "error", icon: "🔥" },
      { type: "closed", label: "Closed", color: "success", icon: "✅" },
      { type: "followup", label: "Follow-up", color: "warning", icon: "🔄" },
      { type: "dead", label: "Dead", color: "default", icon: "💀" },
      { type: "all", label: "All Leads", color: "primary", icon: "📊" },
    ];
  };

  // Quick time periods
  const getTimePeriodButtons = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    let lastMonth = currentMonth - 1;
    let lastMonthYear = currentYear;
    if (lastMonth === 0) {
      lastMonth = 12;
      lastMonthYear = currentYear - 1;
    }

    return [
      {
        label: "Current Month",
        month: currentMonth,
        year: currentYear,
        description: `${months[currentMonth - 1]?.label} ${currentYear}`,
        icon: <Rocket fontSize="small" />
      },
      {
        label: "Last Month",
        month: lastMonth,
        year: lastMonthYear,
        description: `${months[lastMonth - 1]?.label} ${lastMonthYear}`,
        icon: <TrendingUp fontSize="small" />
      },
    ];
  };

  const handleQuickTimePeriod = (period) => {
    setSelectedMonth(period.month);
    setSelectedYear(period.year);
    setSuccess(`Time period set to ${period.description}`);
  };

  // --- Summary Calculations ---
  const summaryData = React.useMemo(() => {
    let totals = {
      totalLeads: leadsData.length,
      totalQuotation: 0,
      totalReceived: 0,
      totalOverdue: 0
    };

    leadsData.forEach(lead => {
      // 1. Calculate Total Approved Quotation Amount
      // Use lead-level fields which are more reliable
      let approvedAmount = Number(lead.approvedAmount) || Number(lead.totalDealValue) || Number(lead.quotationData?.finalAmount) || 0;

      // Fallback to searching quotations if approvedAmount is still 0
      if (approvedAmount === 0 && (lead.quotationStatus === 'quotation_approved' || lead.status === 'quotation_approved')) {
        let approvedQuotation = lead.quotations?.find(q => q.status === 'approved' || q.status === 'accepted' || q.status === 'quotation_approved');

        // 🚨 DEEP FALLBACK: If lead is approved but no quotation is explicitly marked approved,
        // use the activeQuotation or the first quotation as the source of truth for the amount.
        if (!approvedQuotation && lead.quotations?.length > 0) {
          const activeQId = lead.activeQuotation?.$oid || (typeof lead.activeQuotation === 'string' ? lead.activeQuotation : null);
          approvedQuotation = lead.quotations.find(q => (q._id?.$oid || q._id) === activeQId) || lead.quotations[0];
          console.log(`📊 Deep fallback for lead ${lead.clientName}: using quotation ${approvedQuotation?._id?.$oid || approvedQuotation?._id}`);
        }

        approvedAmount = Number(approvedQuotation?.originalApprovedAmount) || Number(approvedQuotation?.finalAmount) || 0;
      }
      totals.totalQuotation += approvedAmount;

      // 2. Calculate Total Received Amount
      let received = Number(lead.totalReceived) || Number(lead.amountReceived) || Number(lead.paidAmount) || 0;
      if (received === 0 && lead.quotations && Array.isArray(lead.quotations)) {
        // Find quotation for received amount - use same robust logic
        let approvedQ = lead.quotations.find(q => q.status === 'approved' || q.status === 'quotation_approved');
        if (!approvedQ && (lead.quotationStatus === 'quotation_approved' || lead.status === 'quotation_approved')) {
          const activeQId = lead.activeQuotation?.$oid || (typeof lead.activeQuotation === 'string' ? lead.activeQuotation : null);
          approvedQ = lead.quotations.find(q => (q._id?.$oid || q._id) === activeQId) || lead.quotations[0];
        }

        if (approvedQ && approvedQ.payments) {
          received = approvedQ.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
        } else if (approvedQ) {
          received = Number(approvedQ.totalReceived) || Number(approvedQ.paidAmount) || 0;
        }
      }
      totals.totalReceived += received;

      // 3. Calculate Overdue
      // 🚨 RULE: Standardized "Payment Started" rule
      let overdue = 0;
      if (approvedAmount > 0 && received > 0) {
        overdue = Math.max(0, approvedAmount - received);
      }

      // Fallback to lead field if the above logic missed it but lead is explicitly overdue
      if (overdue === 0 && (lead.status === 'payment_overdue' || lead.paymentStatus === 'overdue')) {
        overdue = Number(lead.overdueAmount) || 0;
      }

      totals.totalOverdue += overdue;
    });

    return totals;
  }, [leadsData]);

  // Helper to get next payment date for payment-related leads
  const getNextPaymentDate = (lead) => {
    if (lead.quotations && lead.quotations.length > 0) {
      const latestQuotation = lead.quotations[lead.quotations.length - 1];
      if (latestQuotation.payments && latestQuotation.payments.length > 0) {
        const allPayments = latestQuotation.payments
          .filter(p => p.nextPaymentDate)
          .sort((a, b) => new Date(a.nextPaymentDate) - new Date(b.nextPaymentDate));

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const nextPayment = allPayments.find(p =>
          new Date(p.nextPaymentDate) >= today
        );

        if (nextPayment) return new Date(nextPayment.nextPaymentDate);
        if (allPayments.length > 0) return new Date(allPayments[allPayments.length - 1].nextPaymentDate);
      }
      if (latestQuotation.nextPaymentDate) return new Date(latestQuotation.nextPaymentDate);
    }
    return null;
  };

  const getPrimaryTargetDate = (lead) => {
    if (!lead) return { date: null, label: "" };

    // 🚨 RULE: For fully paid leads, show leadCallDate only IF they are not actively rescheduled/meeting
    if ((lead.status === "fully_paid" || lead.paymentStatus === "fully_paid") &&
      lead.status !== "rescheduled" &&
      lead.status !== "meeting_scheduled") {
      return {
        date: lead.leadCallDate ? new Date(lead.leadCallDate) : null,
        label: ""
      };
    }

    const candidates = [];

    const nextPayment = getNextPaymentDate(lead);
    if (nextPayment) {
      candidates.push({
        date: nextPayment,
        label: "Next payment"
      });
    }

    if (lead.meeting?.dateTime) {
      candidates.push({
        date: new Date(lead.meeting.dateTime),
        label: "Meeting on"
      });
    }

    if (lead.rescheduledDate) {
      candidates.push({
        date: new Date(lead.rescheduledDate),
        label: "Rescheduled to"
      });
    }

    if (candidates.length === 0) {
      return {
        date: lead.leadCallDate ? new Date(lead.leadCallDate) : null,
        label: ""
      };
    }

    candidates.sort((a, b) => b.date.getTime() - a.date.getTime());
    return candidates[0];
  };

  const getDateTypeLabel = (lead) => {
    return getPrimaryTargetDate(lead).label;
  };

  // Helper functions for table display (same as leads screen)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getDisplayStatus = (status) => {
    return statusDisplayMap[status] || status?.charAt(0)?.toUpperCase() + status?.slice(1) || "Unknown";
  };

  // Status color mapping (same as leads screen)
  const getStatusChipColor = (status, leadStatus) => {
    if (status === "closed") return "warning";
    if (status === "hot") return "info";
    if (status === "warm") return "info";
    if (status === "dead") return "default";
    if (status === "quotation_approved") return "success";
    if (status === "quotation_revised") return "info";
    if (status === "quotation_rejected") return "error";
    if (status === "payment_overdue" || status === "overdue") return "error";
    if (status === "meeting_scheduled") return "secondary";
    if (status === "follow-up") return "warning";
    if (status === "negotiation") return "warning";
    if (status === "rescheduled") return "info";
    if (status === "quotation_shared") return "success";
    if (status === "token_received" || status === "token_paid") return "success";
    if (status === "partially_paid") return "warning";
    if (status === "fully_paid") return "success";
    if (status === "pending" || status === "payment_pending") return "default";
    return "default";
  };

  // Call status icon mapping (same as leads screen)
  const getCallStatusIcon = (callStatus) => {
    switch (callStatus) {
      case "received":
        return <Phone sx={{ color: "success.main", fontSize: 16 }} />;
      case "call_not_received":
        return <PhoneDisabled sx={{ color: "error.main", fontSize: 16 }} />;
      case "switch_off":
        return <PhoneCallback sx={{ color: "orange", fontSize: 16 }} />;
      case "wrong_number":
        return <PhoneForwarded sx={{ color: "red", fontSize: 16 }} />;
      default:
        return <Phone sx={{ color: "grey", fontSize: 16 }} />;
    }
  };

  const getCallStatusText = (callStatus) => {
    return statusDisplayMap[callStatus] || callStatus;
  };

  if (loadingCompanies) {
    return (
      <Box sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "60vh",
        bgcolor: "background.default"
      }}>
        <CircularProgress sx={{ color: "primary.main" }} />
        <Typography variant="body2" sx={{ ml: 2, color: "text.primary" }}>
          Loading company information...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: "100vh",
      bgcolor: "background.default",
      py: { xs: 2, md: 3 },
      mt: -2.5,
      ml: -3,
    }}>
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2 } }}>

        {/* Header Section with Title and Download Button */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <AnalyticsIcon color="primary" /> Reports Dashboard
          </Typography>

          <Button
            variant="contained"
            size="medium"
            startIcon={exporting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DownloadIcon />}
            onClick={handleExportExcel}
            disabled={exporting || (role === 'admin' && !adminCompany)}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 600,
              fontSize: '0.85rem',
              borderRadius: 2,
              bgcolor: "primary.main",
              boxShadow: `0 4px 12px ${alpha(primaryColor, 0.15)}`,
              '&:hover': {
                bgcolor: "primary.dark",
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 16px ${alpha(primaryColor, 0.25)}`,
              },
              transition: 'all 0.2s ease',
            }}
          >
            {exporting ? 'Exporting...' : 'Download Excel Report'}
          </Button>
        </Box>

        {/* Main Configuration Card - Horizontal Layout */}
        <Card sx={{
          mb: 3,
          borderRadius: 2,
          overflow: 'visible',
          bgcolor: 'background.paper',
          border: `1px solid ${themeBorder}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2} alignItems="center">
              {/* Report Period Label - Clearer for Users */}
              <Grid item xs={12} lg={2.5}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                    <CalendarMonth sx={{ fontSize: 18 }} /> Report Period
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: { xs: 'none', lg: 'block' } }}>
                    Select month & year
                  </Typography>
                </Box>
              </Grid>

              {/* Selectors Column */}
              <Grid item xs={12} lg={9.5}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                  width: '100%'
                }}>
                  {/* Company Info/Selector */}
                  {(role === "superAdmin" || role === "admin") && (
                    <Box sx={{ minWidth: { xs: '100%', sm: 200 }, flexShrink: 0 }}>
                      {role === "superAdmin" ? (
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ fontSize: '0.8rem' }}>Company</InputLabel>
                          <Select
                            value={selectedCompany}
                            label="Company"
                            onChange={(e) => setSelectedCompany(e.target.value)}
                            sx={{ borderRadius: 1.5, fontSize: '0.85rem' }}
                          >
                            <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>All Companies</MenuItem>
                            {companies.map((company) => (
                              <MenuItem key={company._id} value={company._id} sx={{ fontSize: '0.8rem' }}>
                                {company.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (role === "admin" && adminCompany) && (
                        <Box sx={{
                          p: 0.8,
                          px: 2,
                          bgcolor: alpha(primaryColor, 0.04),
                          borderRadius: 1.5,
                          border: `1px solid ${alpha(primaryColor, 0.1)}`,
                          display: 'flex',
                          flexDirection: 'column',
                          minWidth: 150
                        }}>
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>🏢 Company</Typography>
                          <Typography variant="body2" fontWeight={700} noWrap sx={{ color: primaryColor, fontSize: '0.8rem' }}>
                            {adminCompany.name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Month Selector - Increased width */}
                  <Box sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.8rem' }}>Month</InputLabel>
                      <Select
                        value={selectedMonth}
                        label="Month"
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        sx={{ borderRadius: 1.5, fontSize: '0.85rem' }}
                      >
                        {months.map((month) => (
                          <MenuItem key={month.value} value={month.value} sx={{ fontSize: '0.8rem' }}>
                            {month.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Year Selector - Increased width */}
                  <Box sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.8rem' }}>Year</InputLabel>
                      <Select
                        value={selectedYear}
                        label="Year"
                        onChange={(e) => setSelectedYear(e.target.value)}
                        sx={{ borderRadius: 1.5, fontSize: '0.85rem' }}
                      >
                        {years.map((year) => (
                          <MenuItem key={year} value={year} sx={{ fontSize: '0.8rem' }}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Quick Select Chips - Positioned at Right */}
                  <Box sx={{ display: 'flex', gap: 1, ml: { sm: 1 } }}>
                    {getTimePeriodButtons().map((period, index) => (
                      <Chip
                        key={index}
                        label={period.label.split(' ')[0]}
                        size="small"
                        variant={selectedMonth === period.month && selectedYear === period.year ? "filled" : "outlined"}
                        onClick={() => handleQuickTimePeriod(period)}
                        color={selectedMonth === period.month && selectedYear === period.year ? "primary" : "default"}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 28,
                          borderRadius: '14px',
                          px: 0.5
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* QUICK ACTIONS CARD */}
        <Card sx={{
          mb: 3,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          border: `1px solid ${themeBorder}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: "text.primary",
                fontSize: '0.9rem'
              }}>
                <Visibility sx={{ fontSize: 18 }} /> Quick View Filters
              </Typography>

              {/* Active Filter Chip moved inside the card to save space */}
              {activeFilter && (
                <Chip
                  label={`Filter: ${getQuickActionButtons().find(btn => btn.type === activeFilter)?.label || activeFilter}`}
                  onDelete={clearFilter}
                  deleteIcon={<Clear sx={{ fontSize: 14 }} />}
                  color="primary"
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: '24px',
                    borderColor: alpha(primaryColor, 0.3)
                  }}
                />
              )}
            </Box>
            <Grid container spacing={1.5} justifyContent="center">
              {getQuickActionButtons().map((button) => (
                <Grid item key={button.type} xs={6} sm={3} md={2.4}>
                  <Button
                    fullWidth
                    variant={activeFilter === button.type ? "contained" : "outlined"}
                    onClick={() => fetchLeadsData(button.type)}
                    disabled={loadingLeads}
                    sx={{
                      py: 1,
                      borderRadius: 1.5,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      ...(activeFilter === button.type ? {
                        bgcolor: primaryColor,
                        color: '#fff',
                        '&:hover': { bgcolor: primaryColor }
                      } : {
                        borderColor: themeBorder,
                        color: 'text.secondary',
                        '&:hover': { borderColor: primaryColor, bgcolor: themeLight }
                      }),
                      transition: 'all 0.2s'
                    }}
                  >
                    {button.icon} {button.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards Section - Flex Space Between & Smaller Text */}
        {activeFilter && leadsData.length > 0 && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: 4
          }}>
            {/* Total Leads Card */}
            <Box sx={{ width: { xs: '100%', sm: '48%', md: '23.5%' } }}>
              <Card sx={{
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${themeBorder}`,
                borderLeft: `4px solid ${primaryColor}`,
                backgroundColor: alpha(primaryColor, 0.02),
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: primaryColor,
                  transform: 'translateY(-2px)',
                }
              }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <GroupsIcon sx={{ color: primaryColor, fontSize: 18 }} />
                    <Typography variant="caption" color="primary.main" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                      Total Leads
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600} color="text.primary">
                    {summaryData.totalLeads.toLocaleString('en-IN')}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Total Quotation Amount */}
            <Box sx={{ width: { xs: '100%', sm: '48%', md: '23.5%' } }}>
              <Card sx={{
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${themeBorder}`,
                borderLeft: `4px solid ${theme.palette.info.main}`,
                backgroundColor: alpha(theme.palette.info.main, 0.02),
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: theme.palette.info.main,
                  transform: 'translateY(-2px)',
                }
              }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AssignmentTurnedIn sx={{ color: theme.palette.info.main, fontSize: 18 }} />
                    <Typography variant="caption" color="info.main" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                      Quotation
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600} color="text.primary">
                    ₹{summaryData.totalQuotation.toLocaleString('en-IN')}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Total Received Amount */}
            <Box sx={{ width: { xs: '100%', sm: '48%', md: '23.5%' } }}>
              <Card sx={{
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${themeBorder}`,
                borderLeft: `4px solid ${theme.palette.success.main}`,
                backgroundColor: alpha(theme.palette.success.main, 0.02),
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: theme.palette.success.main,
                  transform: 'translateY(-2px)',
                }
              }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccountBalanceWallet sx={{ color: theme.palette.success.main, fontSize: 18 }} />
                    <Typography variant="caption" color="success.main" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                      Received
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600} color="text.primary">
                    ₹{summaryData.totalReceived.toLocaleString('en-IN')}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Total Overdue */}
            <Box sx={{ width: { xs: '100%', sm: '48%', md: '23.5%' } }}>
              <Card sx={{
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${themeBorder}`,
                borderLeft: `4px solid ${theme.palette.error.main}`,
                backgroundColor: alpha(theme.palette.error.main, 0.02),
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: theme.palette.error.main,
                  transform: 'translateY(-2px)',
                }
              }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ReportProblem sx={{ color: theme.palette.error.main, fontSize: 18 }} />
                    <Typography variant="caption" color="error.main" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                      Overdue
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600} color="text.primary">
                    ₹{summaryData.totalOverdue.toLocaleString('en-IN')}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}


        {/* Leads Data Table - Displayed below the reports card */}
        {leadsData.length > 0 && (
          <Card sx={{
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            border: `1px solid ${themeBorder}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column',
            height: 'auto', // Let content determine height
            minHeight: '200px' // Only minimum height
          }}>
            <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{
                p: 2,
                borderBottom: `1px solid ${themeBorder}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'background.default',
                flexShrink: 0 // Don't shrink header
              }}>
                <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1rem' }}>
                  Leads Report - View Only
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {months.find(m => m.value === selectedMonth)?.label} {selectedYear} •
                  {leadsData.length} records
                </Typography>
              </Box>

              {/* Dynamic Table Container - Takes available space */}
              <TableContainer
                sx={{
                  flex: 1, // Take all available space
                  overflow: 'auto',
                  position: 'relative',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: theme.palette.mode === 'light' ? '#f1f1f1' : '#2d3748',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.mode === 'light' ? '#c1c1c1' : '#4a5568',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: theme.palette.mode === 'light' ? '#a8a8a8' : '#718096',
                  }
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {role === "superAdmin" && (
                        <TableCell sx={{
                          fontWeight: 600,
                          bgcolor: 'background.default',
                          fontSize: '0.8rem',
                          py: 1.5,
                          borderBottom: `2px solid ${themeBorder}`
                        }}>
                          Company
                        </TableCell>
                      )}
                      <TableCell sx={{
                        fontWeight: 600,
                        bgcolor: 'background.default',
                        fontSize: '0.8rem',
                        py: 1.5,
                        borderBottom: `2px solid ${themeBorder}`
                      }}>
                        Client Name
                      </TableCell>
                      <TableCell sx={{
                        fontWeight: 600,
                        bgcolor: 'background.default',
                        fontSize: '0.8rem',
                        py: 1.5,
                        borderBottom: `2px solid ${themeBorder}`
                      }}>
                        Contact No
                      </TableCell>
                      <TableCell sx={{
                        fontWeight: 600,
                        bgcolor: 'background.default',
                        fontSize: '0.8rem',
                        py: 1.5,
                        borderBottom: `2px solid ${themeBorder}`
                      }}>
                        Service Interested
                      </TableCell>
                      <TableCell sx={{
                        fontWeight: 600,
                        bgcolor: 'background.default',
                        fontSize: '0.8rem',
                        py: 1.5,
                        borderBottom: `2px solid ${themeBorder}`
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{
                        fontWeight: 600,
                        bgcolor: 'background.default',
                        fontSize: '0.8rem',
                        py: 1.5,
                        borderBottom: `2px solid ${themeBorder}`
                      }}>
                        Lead Call Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leadsData.map((lead, index) => (
                      <TableRow
                        key={lead._id || index}
                        sx={{
                          '&:last-child td': {
                            borderBottom: '1px solid rgba(224, 224, 224, 1)' // Keep border for last row
                          }
                        }}
                      >
                        {role === "superAdmin" && (
                          <TableCell sx={{
                            fontSize: '0.8rem',
                            py: 1.2,
                            borderBottom: `1px solid ${themeBorder}`
                          }}>
                            {lead.company?.name || 'N/A'}
                          </TableCell>
                        )}
                        <TableCell sx={{
                          fontWeight: "bold",
                          fontSize: '0.8rem',
                          py: 1.2,
                          borderBottom: `1px solid ${themeBorder}`
                        }}>
                          {lead.clientName || 'N/A'}
                        </TableCell>
                        <TableCell sx={{
                          fontSize: '0.8rem',
                          py: 1.2,
                          borderBottom: `1px solid ${themeBorder}`
                        }}>
                          {lead.contactNo || 'N/A'}
                        </TableCell>
                        <TableCell sx={{
                          fontSize: '0.8rem',
                          py: 1.2,
                          borderBottom: `1px solid ${themeBorder}`
                        }}>
                          {Array.isArray(lead.serviceInterested) && lead.serviceInterested.length > 0
                            ? lead.serviceInterested.map(service => service.service || service.name).join(', ')
                            : 'No services'
                          }
                        </TableCell>
                        <TableCell sx={{
                          py: 1.2,
                          borderBottom: `1px solid ${themeBorder}`
                        }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, alignItems: 'flex-start' }}>
                            {/* Classification Status */}
                            <Chip
                              label={getDisplayStatus(lead.classificationStatus || lead.status)}
                              size="small"
                              color={getStatusChipColor(lead.classificationStatus || lead.status)}
                              sx={{
                                fontWeight: "bold",
                                fontSize: '0.7rem',
                                height: '22px'
                              }}
                            />

                            {/* Scheduling Status */}
                            {lead.schedulingStatus && lead.schedulingStatus !== 'none' && (
                              <Chip
                                label={getDisplayStatus(lead.schedulingStatus)}
                                size="small"
                                color={getStatusChipColor(lead.schedulingStatus)}
                                sx={{
                                  fontWeight: "bold",
                                  fontSize: '0.7rem',
                                  height: '22px'
                                }}
                              />
                            )}

                            {/* Quotation Status */}
                            {lead.quotationStatus && lead.quotationStatus !== 'none' && (
                              <Chip
                                label={getDisplayStatus(lead.quotationStatus)}
                                size="small"
                                color={getStatusChipColor(lead.quotationStatus)}
                                sx={{
                                  fontWeight: "bold",
                                  fontSize: '0.7rem',
                                  height: '22px'
                                }}
                              />
                            )}

                            {/* Payment Status */}
                            {lead.paymentStatus &&
                              lead.paymentStatus !== 'none' &&
                              lead.paymentStatus !== 'pending' &&
                              lead.paymentStatus !== 'payment_pending' &&
                              lead.quotations?.length > 0 && (
                                <Chip
                                  label={getDisplayStatus(lead.paymentStatus)}
                                  size="small"
                                  color={getStatusChipColor(lead.paymentStatus)}
                                  sx={{
                                    fontWeight: "bold",
                                    fontSize: '0.7rem',
                                    height: '22px',
                                    border: '1px solid',
                                    borderColor: (lead.paymentStatus === 'overdue' || lead.paymentStatus === 'payment_overdue') ? 'error.main' : 'primary.main',
                                    backgroundColor: (lead.paymentStatus === 'overdue' || lead.paymentStatus === 'payment_overdue') ? 'error.light' : 'inherit',
                                    color: (lead.paymentStatus === 'overdue' || lead.paymentStatus === 'payment_overdue') ? 'error.contrastText' : 'inherit',
                                  }}
                                />
                              )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {getCallStatusIcon(lead.callStatus)}
                              <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                                {getCallStatusText(lead.callStatus)}
                              </Typography>
                            </Box>

                            {lead.noOfTimesCalled > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: "50%",
                                    backgroundColor: "primary.main",
                                    color: mode === "light" ? "#fff" : "#0f172a",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "0.65rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {lead.noOfTimesCalled}
                                </Box>
                                <Typography variant="body2" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                                  calls
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{
                          py: 1.2,
                          borderBottom: `1px solid ${themeBorder}`
                        }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                            {(() => {
                              const { date: displayDate, label: dateLabel } = getPrimaryTargetDate(lead);

                              if (!displayDate) {
                                return (
                                  <Typography variant="body2" sx={{
                                    color: "text.secondary",
                                    fontStyle: "italic",
                                    fontSize: '0.8rem'
                                  }}>
                                    N/A
                                  </Typography>
                                );
                              }

                              return (
                                <>
                                  {dateLabel && (
                                    <Typography variant="caption" sx={{
                                      color: 'secondary.main',
                                      fontWeight: '600',
                                      fontSize: '0.65rem',
                                      textTransform: 'uppercase'
                                    }}>
                                      {dateLabel}
                                    </Typography>
                                  )}
                                  <Typography variant="body2" sx={{
                                    fontWeight: "bold",
                                    fontSize: '0.8rem'
                                  }}>
                                    {formatDate(displayDate)}
                                  </Typography>
                                  {/* <Typography variant="body2" sx={{
                                    color: "primary.main",
                                    fontSize: "0.7rem",
                                    fontWeight: "500"
                                  }}>
                                    {formatTime(displayDate)}
                                  </Typography> */}
                                </>
                              );
                            })()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Table Footer - Fixed at bottom */}
              <Box sx={{
                p: 1.5,
                borderTop: `1px solid ${themeBorder}`,
                backgroundColor: 'background.default',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0 // Don't shrink footer
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Showing {leadsData.length} records
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Report generated on {new Date().toLocaleDateString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Empty State when no data */}
        {leadsData.length === 0 && activeFilter && !loadingLeads && (
          <Card sx={{
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: `1px solid ${themeBorder}`,
            textAlign: 'center',
            p: 4
          }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Data Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No {activeFilter} leads found for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </Typography>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default Reports;